import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { AddPackageRequest } from '@/apis/warehouse';

interface AddPackageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: AddPackageRequest) => void;
  isPending?: boolean;
}

const INITIAL_FORM = {
  weight: '',
  largestDimension: '',
  middleDimension: '',
  smallestDimension: '',
  note: '',
};

type FormKey = keyof typeof INITIAL_FORM;
type DimensionKey = Exclude<FormKey, 'note'>;

const DIMENSION_FIELDS: { key: DimensionKey; label: string; placeholder: string }[] = [
  { key: 'weight', label: 'Weight, lbs', placeholder: 'Weight' },
  { key: 'largestDimension', label: 'Largest dimension, in', placeholder: 'L' },
  { key: 'middleDimension', label: 'Middle dimension, in', placeholder: 'W' },
  { key: 'smallestDimension', label: 'Smallest dimension, in', placeholder: 'H' },
];

const REQUIRED_KEYS = DIMENSION_FIELDS.map((f) => f.key);

export function AddPackageDialog({ open, onOpenChange, onSave, isPending }: AddPackageDialogProps) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<DimensionKey, string>>>({});

  useEffect(() => {
    if (open) {
      setForm(INITIAL_FORM);
      setErrors({});
    }
  }, [open]);

  const handleSave = () => {
    const newErrors: Partial<Record<DimensionKey, string>> = {};
    REQUIRED_KEYS.forEach((key) => {
      const num = Number(form[key]);
      if (!form[key].trim() || Number.isNaN(num) || num <= 0) {
        newErrors[key] = 'Required, must be greater than 0';
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSave({
      weight: Number(form.weight),
      largestDimension: Number(form.largestDimension),
      middleDimension: Number(form.middleDimension),
      smallestDimension: Number(form.smallestDimension),
      note: form.note.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[420px]">
        <DialogHeader className="border-b border-smoke pb-3">
          <DialogTitle className="text-lg font-semibold text-black">Add package</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-3">
          {DIMENSION_FIELDS.map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="mb-1.5 block text-sm text-lightGray">
                {label}
                <span className="ml-0.5 text-danger">*</span>
              </label>
              <div
                className={`rounded-full border focus-within:border-primary-s500 ${
                  errors[key] ? 'border-danger' : 'border-smoke'
                }`}
              >
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder={placeholder}
                  value={form[key]}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, [key]: e.target.value }));
                    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
                  }}
                  className="w-full border-0 bg-transparent px-4 py-2.5 text-sm text-black placeholder:text-lightGray outline-none focus:ring-0"
                />
              </div>
              {errors[key] && <p className="mt-1 text-xs text-danger">{errors[key]}</p>}
            </div>
          ))}

          <div>
            <label className="mb-1.5 block text-sm text-lightGray">Notes</label>
            <textarea
              value={form.note}
              onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
              rows={3}
              placeholder="Enter your note"
              className="w-full resize-none rounded-xl border border-smoke px-4 py-2.5 text-sm text-black placeholder:text-lightGray outline-none focus:border-primary-s500"
            />
          </div>
        </div>

        <div className="mt-2 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full border border-smoke bg-smoke1 px-6 py-2 text-sm text-black hover:bg-smoke transition-colors"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isPending}
            className="rounded-full bg-primary-s500 px-6 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-60"
          >
            {isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
